import { useEffect, useState, useCallback } from "react";
import { http, inr, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ResponsibleManagerCard from "@/pages/admin/ResponsibleManagerCard";
import GuestIdSection from "@/pages/admin/GuestIdSection";

function DRow({ label, value, accent }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-sm" style={{ color: "var(--muted)" }}>{label}</span>
      <span className="font-medium text-sm text-right" style={{ color: accent ? "var(--terracotta)" : "var(--fg)" }}>{value}</span>
    </div>
  );
}

function Dialog({ title, onClose, children, testid, wide }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.45)" }} data-testid={testid}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card w-full" style={{ maxWidth: wide ? 680 : 500, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">{title}</h3>
          <button onClick={onClose} className="btn-outline" style={{ padding: "4px 12px", fontSize: 13 }} data-testid={`${testid}-close`}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------------- GUEST DETAIL / EDIT / MERGE / DELETE ---------------- */
export function GuestDetailDialog({ guest, onClose, onChanged }) {
  const { user } = useAuth();
  const isOwner = user?.role === "SUPER_ADMIN";
  const [d, setD] = useState(null);
  const [mode, setMode] = useState("view");
  const [form, setForm] = useState(null);
  const [others, setOthers] = useState([]);
  const [mergeTarget, setMergeTarget] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    http.get(`/admin/guests/${guest.id}`).then((r) => {
      setD(r.data);
      const g = r.data.guest;
      setForm({ full_name: g.full_name, mobile: g.mobile || "", email: g.email || "", date_of_birth: g.date_of_birth || "", notes: g.notes || "", marketing_consent: !!g.marketing_consent });
    }).catch((e) => toast.error(fmtErr(e)));
  }, [guest.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isOwner) http.get("/admin/guests").then((r) => setOthers(r.data.guests.filter((x) => x.id !== guest.id))).catch(() => {});
  }, [isOwner, guest.id]);

  const saveEdit = async () => {
    setBusy(true);
    try {
      await http.put(`/admin/guests/${guest.id}`, form);
      toast.success("Guest profile updated");
      setMode("view"); load(); onChanged && onChanged();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const doMerge = async () => {
    if (!mergeTarget) return;
    if (!window.confirm(`Merge "${d.guest.full_name}" INTO the selected guest? All stays, orders, services and payments move to the kept profile. This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await http.post("/admin/guests/merge", { source_id: guest.id, target_id: mergeTarget });
      toast.success(`Merged — ${r.data.stays} stays, ${r.data.orders} orders, ${r.data.service_requests} requests moved`);
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(`DELETE TEST DATA: permanently remove guest "${d.guest.full_name}" and all linked test stays/orders/services? Blocked automatically if real payments or fulfilled work exist.`)) return;
    setBusy(true);
    try {
      const r = await http.delete(`/admin/guests/${guest.id}`);
      toast.success(`Test dataset deleted (${r.data.stays_removed} stays cleaned)`);
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  if (!d || !form) return <Dialog title="Guest" onClose={onClose} testid="guest-dialog"><p className="text-sm">Loading…</p></Dialog>;
  const g = d.guest;

  return (
    <Dialog title={g.full_name} onClose={onClose} testid="guest-dialog" wide>
      <div className="flex gap-2 mb-4 flex-wrap">
        <span className="badge-pine">{d.total_stays} stays</span>
        <span className="badge-pine">{d.total_nights} nights</span>
        <span className="badge-pine">{inr(d.total_spend)} spend</span>
      </div>

      {mode === "view" && (
        <>
          <div className="space-y-2 mb-4">
            <DRow label="Mobile" value={g.mobile || "—"} />
            <DRow label="Email" value={g.email || "—"} />
            <DRow label="Date of birth" value={g.date_of_birth || "—"} />
            <DRow label="Marketing consent" value={g.marketing_consent ? "Yes" : "No"} />
            {g.notes && <DRow label="Notes" value={g.notes} />}
          </div>
          <div className="eyebrow mb-2">Stay history</div>
          <div className="mb-4 max-h-48 overflow-auto">
            {d.stays.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No stays.</p>}
            {d.stays.map((s) => (
              <div key={s.id} className="text-sm py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`guest-stay-${s.id}`}>
                {s.booking_reference || s.check_in_date} · {s.check_in_date} → {s.check_out_date} · {s.status} · paid {inr(s.bill.amount_paid)}
              </div>
            ))}
          </div>
          {isOwner && (
            <div className="flex flex-wrap gap-2">
              <button data-testid="guest-edit-btn" onClick={() => setMode("edit")} className="btn-outline">Edit Profile</button>
              <button data-testid="guest-merge-btn" onClick={() => setMode("merge")} className="btn-outline">Merge Duplicate…</button>
              <button data-testid="guest-delete-btn" onClick={() => setMode("delete")} className="btn-outline" style={{ color: "var(--terracotta)" }}>Delete Test Data</button>
            </div>
          )}
        </>
      )}

      {mode === "edit" && (
        <>
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <input data-testid="guest-edit-name" placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            <input data-testid="guest-edit-mobile" placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            <input data-testid="guest-edit-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="guest-edit-dob" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
          </div>
          <textarea data-testid="guest-edit-notes" rows="2" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mb-3" />
          <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
            <input type="checkbox" style={{ width: "auto" }} data-testid="guest-edit-consent" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} />
            Marketing consent
          </label>
          <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>Lifetime stays/nights/spend are always calculated from real history — never editable.</p>
          <div className="flex gap-3">
            <button data-testid="guest-edit-save" onClick={saveEdit} disabled={busy} className="btn-primary flex-1">{busy ? "Saving…" : "Save"}</button>
            <button onClick={() => setMode("view")} className="btn-outline flex-1">Back</button>
          </div>
        </>
      )}

      {mode === "merge" && (
        <>
          <p className="text-sm mb-3" style={{ color: "var(--muted)" }}>Merge this duplicate profile into the correct one. All linked history moves to the kept profile; this profile is removed.</p>
          <select data-testid="guest-merge-target" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} className="mb-4">
            <option value="">Select the profile to KEEP…</option>
            {others.map((x) => <option key={x.id} value={x.id}>{x.full_name} · {x.mobile}</option>)}
          </select>
          <div className="flex gap-3">
            <button data-testid="guest-merge-confirm" onClick={doMerge} disabled={busy || !mergeTarget} className="btn-primary flex-1">Confirm Merge</button>
            <button onClick={() => setMode("view")} className="btn-outline flex-1">Back</button>
          </div>
        </>
      )}

      {mode === "delete" && (
        <>
          <p className="text-sm mb-4" style={{ color: "var(--terracotta)" }}>Permanently delete this TEST guest and all linked test stays, orders, service requests, pending payments and draft bookings? Real financial records will block this automatically.</p>
          <div className="flex gap-3">
            <button data-testid="guest-delete-confirm" onClick={doDelete} disabled={busy} className="btn-primary flex-1" style={{ background: "var(--terracotta)" }}>{busy ? "Deleting…" : "DELETE TEST DATA"}</button>
            <button onClick={() => setMode("view")} className="btn-outline flex-1">Back</button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/* ---------------- STAY DETAIL ---------------- */
export function StayDetailDialog({ stayId, onClose, onChanged }) {
  const { user } = useAuth();
  const isOwner = user?.role === "SUPER_ADMIN";
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { http.get(`/admin/stays/${stayId}`).then((r) => setD(r.data)).catch((e) => toast.error(fmtErr(e))); }, [stayId]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged && onChanged(); };

  const checkout = async () => {
    if (!window.confirm("Manually check out this guest? Bills, orders and payment history are preserved.")) return;
    setBusy(true);
    try {
      await http.post(`/admin/stays/${stayId}/checkout`);
      toast.success("Guest checked out");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  if (!d) return <Dialog title="Stay" onClose={onClose} testid="stay-dialog"><p className="text-sm">Loading…</p></Dialog>;
  const s = d.stay;

  return (
    <Dialog title={`Stay · ${d.guest?.full_name || ""}`} onClose={onClose} testid="stay-dialog" wide>
      <div className="space-y-2 mb-4">
        <DRow label="Status" value={s.status} accent={s.status !== "CHECKED_OUT"} />
        <DRow label="Booking" value={d.booking?.booking_reference || "— (manual / walk-in)"} />
        <DRow label="Rooms" value={d.rooms.map((r) => r.name).join(", ") || "—"} />
        <DRow label="Dates" value={`${s.check_in_date} → ${s.check_out_date}`} />
        <DRow label="Guests" value={s.total_pax} />
        <DRow label="Guest contact" value={`${d.guest?.mobile || "—"} · ${d.guest?.email || "—"}`} />
        {s.actual_check_out && <DRow label="Checked out" value={`${new Date(s.actual_check_out).toLocaleString()}${s.checked_out_by ? ` · by ${s.checked_out_by}` : ""}${s.checkout_method ? ` (${s.checkout_method})` : ""}`} />}
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <ResponsibleManagerCard kind="stays" targetId={s.id} value={s.responsible_manager}
          activeStay={s.status === "CHECKED_IN"} canEdit={isOwner} onSaved={refresh} />
        <GuestIdSection docs={d.guest_ids || []} canDelete={isOwner && s.status === "CHECKED_OUT"} onChanged={refresh} />
      </div>
      <div className="card mb-4" style={{ background: "var(--surface)" }}>
        <div className="eyebrow mb-2">Bill</div>
        <DRow label="Grand total" value={inr(d.bill.grand_total)} />
        <DRow label="Paid" value={inr(d.bill.amount_paid)} />
        <DRow label="Balance" value={inr(d.bill.balance_due)} accent={d.bill.balance_due > 0} />
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-4 text-sm">
        <div>
          <div className="eyebrow mb-1">Food orders ({d.food_orders.length})</div>
          {d.food_orders.map((o) => <div key={o.id} className="py-1 border-b last:border-0" style={{ borderColor: "var(--border)" }}>{o.order_number} · {inr(o.total)} · {o.status}</div>)}
          {d.food_orders.length === 0 && <span style={{ color: "var(--muted)" }}>None</span>}
        </div>
        <div>
          <div className="eyebrow mb-1">Service requests ({d.service_requests.length})</div>
          {d.service_requests.map((r) => <div key={r.id} className="py-1 border-b last:border-0" style={{ borderColor: "var(--border)" }}>{r.service_name_snapshot} × {r.quantity} · {r.status}</div>)}
          {d.service_requests.length === 0 && <span style={{ color: "var(--muted)" }}>None</span>}
        </div>
      </div>
      {s.status === "CHECKED_IN" && (
        <button data-testid="stay-dialog-checkout" onClick={checkout} disabled={busy} className="btn-primary w-full">{busy ? "Processing…" : "Check Out Guest"}</button>
      )}
      {s.status === "CHECKED_OUT" && (
        <p className="text-xs" style={{ color: "var(--muted)" }}>Historical record — stay details are read-only. Any balance above remains collectible via Bills &amp; Payments.</p>
      )}
    </Dialog>
  );
}

/* ---------------- BILL DETAIL + ADJUSTMENTS + REFUNDS ---------------- */
const ADJ_TYPES = ["DISCOUNT", "WAIVER", "CORRECTION", "ADDITIONAL_CHARGE", "OTHER"];

export function BillDetailDialog({ stayId, onClose, onChanged }) {
  const { user } = useAuth();
  const isOwner = user?.role === "SUPER_ADMIN";
  const [d, setD] = useState(null);
  const [adjType, setAdjType] = useState("DISCOUNT");
  const [adjAmt, setAdjAmt] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { http.get(`/admin/stays/${stayId}`).then((r) => setD(r.data)).catch((e) => toast.error(fmtErr(e))); }, [stayId]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged && onChanged(); };

  const addAdjustment = async () => {
    const raw = parseFloat(adjAmt);
    if (isNaN(raw) || raw === 0) return toast.error("Enter a non-zero amount");
    if (!adjReason.trim()) return toast.error("Reason is required");
    const signed = ["DISCOUNT", "WAIVER"].includes(adjType) ? -Math.abs(raw) : adjType === "ADDITIONAL_CHARGE" ? Math.abs(raw) : raw;
    setBusy(true);
    try {
      await http.post(`/admin/stays/${stayId}/adjustments`, { type: adjType, amount: signed, reason: adjReason });
      toast.success("Adjustment added — bill recalculated");
      setAdjAmt(""); setAdjReason(""); refresh();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const reverse = async (a) => {
    if (!window.confirm(`Reverse this adjustment of ${inr(a.amount)}? The original stays in audit history and a counter-entry is created.`)) return;
    try {
      await http.post(`/admin/adjustments/${a.id}/reverse`);
      toast.success("Adjustment reversed");
      refresh();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const refund = async (p) => {
    const amt = window.prompt(`Refund amount for payment of ${inr(p.amount)} (max ${inr(p.amount)}):`, String(p.amount));
    if (amt === null) return;
    const reason = window.prompt("Refund reason (required):") || "";
    if (!reason.trim()) return toast.error("Reason is required");
    try {
      await http.post(`/admin/payments/${p.id}/refund`, { amount: parseFloat(amt), reason });
      toast.success("Refund recorded as a separate transaction — original payment untouched");
      refresh();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!d) return <Dialog title="Bill" onClose={onClose} testid="bill-dialog"><p className="text-sm">Loading…</p></Dialog>;
  const b = d.bill;

  return (
    <Dialog title={`Bill · ${d.guest?.full_name || ""}`} onClose={onClose} testid="bill-dialog" wide>
      <div className="mb-4">
        <div className="eyebrow mb-2">Line items</div>
        {b.lines.map((l, i) => (
          <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`billd-line-${i}`}>
            <span>{l.description}</span>
            <span className="font-medium" style={{ color: l.line_total < 0 ? "var(--pine)" : "var(--fg)" }}>{inr(l.line_total)}</span>
          </div>
        ))}
        {b.lines.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No charges.</p>}
      </div>
      <div className="card mb-4 space-y-1" style={{ background: "var(--surface)" }}>
        <DRow label="Subtotal" value={inr(b.subtotal)} />
        <DRow label="Tax" value={inr(b.tax)} />
        {b.adjustment_total !== 0 && <DRow label="Adjustments" value={inr(b.adjustment_total)} accent />}
        <DRow label="Grand total" value={inr(b.grand_total)} />
        <DRow label="Paid (verified)" value={inr(b.amount_paid)} />
        <DRow label="Balance" value={inr(b.balance_due)} accent={b.balance_due !== 0} />
        <DRow label="Stay status" value={d.stay.status} />
      </div>

      <div className="mb-4">
        <div className="eyebrow mb-2">Payments</div>
        {d.payments.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No payments yet.</p>}
        {d.payments.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-2 text-sm py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`billd-pay-${p.id}`}>
            <span className="font-medium" style={{ color: p.amount < 0 ? "var(--terracotta)" : "var(--pine)" }}>{inr(p.amount)}</span>
            <span className="text-xs" style={{ color: "var(--muted)" }}>{p.payment_method} · {p.status}{p.refund_of ? " · refund" : ""} · {new Date(p.created_at).toLocaleDateString()}</span>
            {isOwner && p.status === "SUCCESS" && p.amount > 0 && p.payment_method !== "REFUND" && (
              <button data-testid={`billd-refund-${p.id}`} onClick={() => refund(p)} className="btn-outline" style={{ padding: "4px 10px", fontSize: 12 }}>Refund…</button>
            )}
          </div>
        ))}
        <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>Verified payments are never edited or deleted — refunds are recorded as separate reversal transactions.</p>
      </div>

      <div className="mb-4">
        <div className="eyebrow mb-2">Adjustments</div>
        {d.adjustments.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>None.</p>}
        {d.adjustments.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-2 text-sm py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`billd-adj-${a.id}`}>
            <span className="font-medium" style={{ color: a.amount < 0 ? "var(--pine)" : "var(--terracotta)" }}>{inr(a.amount)}</span>
            <span className="text-xs" style={{ color: "var(--muted)" }}>{a.type} · {a.reason} · by {a.created_by}{a.reversed ? " · REVERSED" : ""}{a.reversal_of ? " · reversal entry" : ""}</span>
            {isOwner && !a.reversed && !a.reversal_of && (
              <button data-testid={`billd-reverse-${a.id}`} onClick={() => reverse(a)} className="btn-outline" style={{ padding: "4px 10px", fontSize: 12 }}>Reverse</button>
            )}
          </div>
        ))}
      </div>

      {isOwner && (
        <div className="card" style={{ background: "var(--surface)" }} data-testid="add-adjustment-form">
          <div className="eyebrow mb-2">Add Adjustment (owner)</div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <select data-testid="adj-type" value={adjType} onChange={(e) => setAdjType(e.target.value)}>
              {ADJ_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
            </select>
            <input data-testid="adj-amount" type="number" placeholder="Amount (₹)" value={adjAmt} onChange={(e) => setAdjAmt(e.target.value)} />
          </div>
          <input data-testid="adj-reason" placeholder="Reason (required)" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} className="mb-2" />
          <button data-testid="adj-save" onClick={addAdjustment} disabled={busy} className="btn-primary w-full">{busy ? "Saving…" : "Add Adjustment"}</button>
        </div>
      )}
    </Dialog>
  );
}

/* ---------------- FOOD ORDER DETAIL / CORRECT ---------------- */
export function OrderDetailDialog({ order, onClose, onChanged }) {
  const { user } = useAuth();
  const isOwner = user?.role === "SUPER_ADMIN";
  const [qtys, setQtys] = useState(() => Object.fromEntries((order.items || []).map((it) => [it.id, it.quantity])));
  const [busy, setBusy] = useState(false);
  const correctable = isOwner && (order.status === "NEW" || order.status === "ACKNOWLEDGED");

  const saveItems = async () => {
    setBusy(true);
    try {
      await http.put(`/admin/food-orders/${order.id}/items`, { items: Object.entries(qtys).map(([item_id, quantity]) => ({ item_id, quantity: parseInt(quantity) || 1 })) });
      toast.success("Order corrected — bill recalculated at original prices");
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog title={`Order ${order.order_number}`} onClose={onClose} testid="order-dialog">
      <div className="space-y-2 mb-4">
        <DRow label="Guest" value={order.guest_name} />
        <DRow label="Status" value={order.status} />
        <DRow label="Placed" value={new Date(order.created_at).toLocaleString()} />
        {order.special_instruction && <DRow label="Instruction" value={order.special_instruction} />}
        {order.assigned_to && <DRow label="Assigned to" value={order.assigned_to} />}
        {order.cancelled_by && <DRow label="Cancelled by" value={`${order.cancelled_by}${order.cancellation_reason ? ` · ${order.cancellation_reason}` : ""}`} />}
      </div>
      <div className="eyebrow mb-2">Items (original price snapshots)</div>
      <div className="mb-4">
        {(order.items || []).map((it) => (
          <div key={it.id} className="flex items-center gap-2 text-sm py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`order-item-${it.id}`}>
            <span className="flex-1">{it.item_name_snapshot} <span className="text-xs" style={{ color: "var(--muted)" }}>@ {inr(it.unit_price)}</span></span>
            {correctable ? (
              <input data-testid={`order-qty-${it.id}`} type="number" min="1" max="50" value={qtys[it.id]}
                onChange={(e) => setQtys({ ...qtys, [it.id]: e.target.value })} style={{ width: 64, padding: "4px 8px" }} />
            ) : <span>× {it.quantity}</span>}
            <span className="font-medium">{inr(it.total)}</span>
          </div>
        ))}
      </div>
      <div className="space-y-1 mb-4">
        <DRow label="Subtotal" value={inr(order.subtotal)} />
        <DRow label="Tax" value={inr(order.tax)} />
        <DRow label="Total" value={inr(order.total)} accent />
      </div>
      {Array.isArray(order.status_history) && order.status_history.length > 0 && (
        <div className="mb-4">
          <div className="eyebrow mb-1">History</div>
          {order.status_history.map((h, i) => (
            <div key={i} className="text-xs py-1" style={{ color: "var(--muted)" }}>{h.status} · {h.by} · {new Date(h.at).toLocaleString()}</div>
          ))}
        </div>
      )}
      {correctable && (
        <button data-testid="order-items-save" onClick={saveItems} disabled={busy} className="btn-primary w-full">{busy ? "Saving…" : "Save Item Corrections"}</button>
      )}
    </Dialog>
  );
}
